/**
 * CGU — Conditions d'utilisation.
 *
 * Structure inspirée de sites institutionnels français équivalents, adaptée
 * au métier de Lexia School : apprentissage linguistique assisté par IA, avec
 * les avertissements spécifiques (CECRL estimatif, parcours médical =
 * apprentissage linguistique et jamais avis médical).
 */
import LegalShell from "@/components/LegalShell";

function Article({ id, num, title, children }) {
    return (
        <section id={id} className="scroll-mt-28 space-y-3">
            <h2 className="font-serif text-xl md:text-2xl text-ink">
                <span className="text-inkSoft font-normal">{num}.</span> {title}
            </h2>
            <div className="text-[15px] leading-relaxed space-y-3">{children}</div>
        </section>
    );
}

export default function CGU() {
    return (
        <LegalShell
            testid="page-cgu"
            eyebrow="Conditions d'utilisation"
            title="Conditions générales d'utilisation."
            subtitle="Dernière mise à jour : février 2026."
        >
            <div
                role="note"
                className="rounded-2xl border border-terracotta/40 bg-terracotta/8 p-4 flex gap-3"
                data-testid="cgu-avertissement"
            >
                <span className="text-terracotta text-lg leading-none">⚠</span>
                <p className="text-sm text-ink">
                    <strong>Avertissement important —</strong> Lexia School est un outil d&apos;apprentissage
                    linguistique. Les niveaux CECRL affichés sont estimatifs et non certifiants. Les parcours
                    professionnels (dont l&apos;anglais médical) sont conçus pour l&apos;acquisition de vocabulaire
                    et de tournures ; ils ne constituent en aucun cas un avis médical, juridique ou professionnel.
                </p>
            </div>

            <Article id="mentions" num="1" title="Mentions légales">
                <p><strong>Éditeur de l&apos;application :</strong> Lexia School.</p>
                <p>Adresse de contact : <a href="mailto:support@openmeta.fr" className="text-terracotta hover:underline">support@openmeta.fr</a>.</p>
                <p><strong>Hébergement :</strong> infrastructure cloud managée. La liste exacte des sous-traitants
                    techniques (LLM, TTS, stockage) figure dans la Politique de confidentialité.</p>
            </Article>

            <Article id="objet" num="2" title="Objet">
                <p>
                    Les présentes Conditions Générales d&apos;Utilisation (« CGU ») encadrent l&apos;accès et
                    l&apos;utilisation de l&apos;application web <em>Lexia School</em> par toute personne
                    disposant d&apos;un compte utilisateur (« l&apos;Utilisateur »). Elles définissent les droits et
                    obligations respectifs de l&apos;Utilisateur et de l&apos;Éditeur.
                </p>
                <p>
                    L&apos;utilisation de l&apos;Application vaut acceptation sans réserve des présentes CGU.
                </p>
            </Article>

            <Article id="acces" num="3" title="Conditions d'accès">
                <p>L&apos;accès à Lexia School nécessite :</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>un navigateur web moderne (Chrome, Firefox, Safari, Edge) à jour ;</li>
                    <li>une connexion Internet ;</li>
                    <li>un microphone pour les fonctionnalités vocales (optionnel) ;</li>
                    <li>la création d&apos;un compte utilisateur (email + mot de passe).</li>
                </ul>
                <p>
                    L&apos;Utilisateur est responsable de la confidentialité de ses identifiants et de toute
                    activité effectuée depuis son compte.
                </p>
            </Article>

            <Article id="usage" num="4" title="Usage acceptable">
                <p>L&apos;Utilisateur s&apos;engage à ne pas :</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>utiliser l&apos;Application à des fins illégales ou contraires à l&apos;ordre public ;</li>
                    <li>tenter d&apos;accéder à des zones réservées, contourner les mécanismes d&apos;authentification,
                        procéder à du <em>scraping</em> automatisé ou à des attaques par déni de service ;</li>
                    <li>diffuser via les fonctionnalités de conversation IA des contenus haineux, harcelants,
                        pornographiques, ou portant atteinte à la vie privée d&apos;autrui ;</li>
                    <li>utiliser l&apos;Application pour générer des contenus destinés à tromper des tiers.</li>
                </ul>
                <p>
                    Le non-respect de ces règles peut entraîner la suspension immédiate du compte, sans préavis.
                </p>
            </Article>

            <Article id="contenu-ia" num="5" title="Contenu généré par intelligence artificielle">
                <p>
                    Les leçons, phrases d&apos;exemple, podcasts, dialogues et corrections proposés par Lexia School
                    sont générés par des modèles d&apos;intelligence artificielle (OpenAI, Anthropic, Google) et
                    peuvent comporter des imprécisions, des erreurs de traduction, ou refléter des biais culturels
                    présents dans les corpus d&apos;entraînement.
                </p>
                <p>
                    L&apos;Utilisateur reconnaît que :
                </p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>
                        <strong>Le niveau CECRL affiché est estimatif</strong>, calculé à partir du test de placement
                        et de la progression dans l&apos;Application. Il ne constitue en aucun cas une certification
                        officielle au sens du Cadre européen commun de référence pour les langues.
                    </li>
                    <li>
                        <strong>Les parcours professionnels (dont l&apos;anglais médical, juridique, etc.) sont des
                            parcours d&apos;apprentissage linguistique.</strong> Ils ne délivrent aucun conseil
                        professionnel, médical, juridique ou financier. Toute décision opérationnelle prise à partir
                        des contenus de l&apos;Application relève de la seule responsabilité de l&apos;Utilisateur.
                    </li>
                    <li>
                        Les corrections de prononciation, les évaluations de compétence et les feedbacks
                        conversationnels sont des estimations et non des vérités absolues.
                    </li>
                </ul>
            </Article>

            <Article id="pi" num="6" title="Propriété intellectuelle">
                <p>
                    L&apos;Éditeur est titulaire des droits sur l&apos;architecture, le code, les visuels, les
                    parcours et l&apos;identité de marque de Lexia School. Toute reproduction, adaptation ou
                    redistribution sans autorisation écrite est interdite.
                </p>
                <p>
                    Les contenus générés à la volée par IA à partir des prompts standards de l&apos;Application
                    (leçons, dialogues, corrections) restent utilisables librement par l&apos;Utilisateur dans un
                    cadre pédagogique personnel.
                </p>
                <p>
                    Les certificats délivrés par Lexia School comportent un identifiant unique et un lien public de
                    vérification. Toute falsification est passible de poursuites.
                </p>
            </Article>

            <Article id="responsabilite" num="7" title="Limitation de responsabilité">
                <p>
                    L&apos;Éditeur est tenu à une obligation de moyens, pas de résultat. L&apos;Application peut
                    connaître des interruptions techniques, notamment lors des mises à jour ou de la maintenance des
                    services tiers utilisés (LLM, TTS, stockage).
                </p>
                <p>
                    L&apos;Éditeur ne saurait être tenu responsable :
                </p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>de la qualité pédagogique ou linguistique des contenus générés à la volée par IA ;</li>
                    <li>de l&apos;absence de résultat sur un examen officiel ou une certification externe ;</li>
                    <li>des décisions prises par l&apos;Utilisateur sur la base d&apos;informations issues de
                        l&apos;Application, en particulier dans les parcours professionnels.</li>
                </ul>
            </Article>

            <Article id="rgpd" num="8" title="Données personnelles">
                <p>
                    Le traitement des données personnelles est décrit dans la{" "}
                    <a href="/confidentialite" className="text-terracotta hover:underline">Politique de confidentialité</a>,
                    qui fait partie intégrante des présentes CGU.
                </p>
            </Article>

            <Article id="resiliation" num="9" title="Résiliation du compte">
                <p>
                    L&apos;Utilisateur peut demander la suppression de son compte à tout moment en écrivant à{" "}
                    <a href="mailto:support@openmeta.fr" className="text-terracotta hover:underline">support@openmeta.fr</a>.
                    Toutes les données associées seront effacées dans un délai raisonnable, sauf obligations légales
                    de conservation.
                </p>
                <p>
                    L&apos;Éditeur peut résilier un compte à tout moment en cas de manquement grave aux présentes
                    CGU ou d&apos;usage abusif.
                </p>
            </Article>

            <Article id="modifications" num="10" title="Modifications des CGU">
                <p>
                    L&apos;Éditeur peut modifier les présentes CGU à tout moment. Les évolutions substantielles font
                    l&apos;objet d&apos;une notification à la prochaine connexion. La poursuite de l&apos;utilisation
                    après notification vaut acceptation.
                </p>
            </Article>

            <Article id="droit" num="11" title="Droit applicable">
                <p>
                    Les présentes CGU sont régies par le droit français. À défaut de règlement amiable, tout litige
                    relatif à Lexia School relèvera de la compétence des tribunaux français.
                </p>
            </Article>

            <div className="pt-6 border-t border-lexiBorder text-sm text-inkSoft">
                <p>
                    Pour toute question relative à ces conditions, contactez-nous à{" "}
                    <a href="mailto:support@openmeta.fr" className="text-terracotta hover:underline">support@openmeta.fr</a>
                    {" "}ou via le formulaire de{" "}
                    <a href="/contact" className="text-terracotta hover:underline">contact</a>.
                </p>
            </div>
        </LegalShell>
    );
}
